import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { setActiveWorkoutExerciseSetWeightOverride } from 'redux/slices/activeWorkout'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'

export default function LoadCompleted({ 
    profileType,
    groupId,
    athleteId,
    phaseIndex, 
    exerciseIndex, 
    exercise,
    setIndex, 
    set, 
    isCompleted, 
    isSkipped,
    showLabel = true
}) {

    const dispatch = useDispatch()

    const disableCardInputs = isCompleted || isSkipped

    var weightRecommendationLoaded = false
    if (profileType === 'group' || profileType === 'group-individual') weightRecommendationLoaded = set.recommendationData.weightRecommendationLoaded
    if (profileType === 'individual') weightRecommendationLoaded = set.recommendationData.weightRecommendationLoaded

    var weightRecommendationRounded = 0
    if (profileType === 'group') weightRecommendationRounded = set.recommendationData.weightRecommendationRounded || 0
    if (profileType === 'group-individual') weightRecommendationRounded = set.groupIndividualRecommendationData[athleteId].weightRecommendationRounded
    if (profileType === 'individual') weightRecommendationRounded = set.recommendationData.weightRecommendationRounded || 0

    var labelWeightRecommendationRounded = 0
    if (profileType === 'group') labelWeightRecommendationRounded = set.recommendationData.labelWeightRecommendationRounded || 0
    if (profileType === 'group-individual') labelWeightRecommendationRounded = set.groupIndividualRecommendationData[athleteId].labelWeightRecommendationRounded
    if (profileType === 'individual') labelWeightRecommendationRounded = set.recommendationData.labelWeightRecommendationRounded || 0

    const [isScaling, setIsScaling] = useState(false)
    const [isInputBoxCurrentlyFocused, setIsInputBoxCurrentlyFocused] = useState(false)
    const [inputBoxValue, setInputBoxValue] = useState(weightRecommendationRounded)

    // Increment/decrement functions

    const DecrementLoadCompleted = () => {

        const newWeight = Math.max(0, weightRecommendationRounded - 5)

        const payload = {
            profileType: profileType,
            groupId: groupId,
            athleteId: athleteId,
            phaseIndex: phaseIndex,
            exerciseIndex: exerciseIndex,
            setIndex: setIndex,
            weightRecommendation: newWeight,
            weightRecommendationRounded: newWeight,
            override: newWeight - (weightRecommendationRounded || 0)
        }

        dispatch(setActiveWorkoutExerciseSetWeightOverride(payload))

        TriggerScaleAnimation()
    }

    const IncrementLoadCompleted = () => {

        const newWeight = weightRecommendationRounded + 5

        const payload = {
            profileType: profileType,
            groupId: groupId,
            athleteId: athleteId,
            phaseIndex: phaseIndex,
            exerciseIndex: exerciseIndex,
            setIndex: setIndex,
            weightRecommendation: newWeight,
            weightRecommendationRounded: newWeight,
            override: newWeight - (weightRecommendationRounded || 0)
        }

        dispatch(setActiveWorkoutExerciseSetWeightOverride(payload))

        TriggerScaleAnimation()
    }

    const TriggerScaleAnimation = () => {
        setIsScaling(true)
        setTimeout(() => setIsScaling(false), 200)
    }

    // Input box functions

    const HandleInputBoxFocus = () => {
        if (disableCardInputs) return

        setIsInputBoxCurrentlyFocused(true)
        setInputBoxValue('')
    }

    const HandleLoadCompletedChange = (value) => {
        if (disableCardInputs) return

        // Allow empty string
        if (value === '') {
            setInputBoxValue('')
            return
        }

        // Only allow digits, max 3 characters
        const numericValue = value.replace(/[^0-9]/g, '')

        if (numericValue.length > 3) return

        // Only update local state - no Redux dispatching
        setInputBoxValue(numericValue)
    }

    const HandleLoadCompletedBlur = () => {
        if (disableCardInputs) return

        // If input is empty, just reset focus state (don't update Redux)
        if (inputBoxValue === '') {
            setIsInputBoxCurrentlyFocused(false)
            return
        }

        // Get raw value 
        
        let rawValue = parseInt(inputBoxValue, 10) || 0
        let rawValueClamped = Math.max(0, Math.min(rawValue, 500)); // 500 lb max input

        // Get rounded value

        let roundedValue = Math.round(rawValueClamped / 5) * 5;

        // Calculate override amount

        let overrideAmount = (roundedValue - labelWeightRecommendationRounded) / 5

        // Set payload values

        const weightOverridePayload = {
            profileType: profileType,
            groupId: groupId,
            athleteId: athleteId,
            phaseIndex: phaseIndex,
            exerciseIndex: exerciseIndex,
            setIndex: setIndex,
            weightRecommendation: parseFloat(rawValue),
            weightRecommendationRounded: parseInt(roundedValue),
            override: parseInt(overrideAmount)
        }

        dispatch(setActiveWorkoutExerciseSetWeightOverride(weightOverridePayload))

        // Reset local state
        setInputBoxValue('')
        setIsInputBoxCurrentlyFocused(false)
    }

    if (isSkipped === true) {
        return null
    }

    if (exercise.exerciseType === 'bodyweight') {
        return null
    }

    if (weightRecommendationLoaded === false) {
        return (
            <div className='space-y-2'>
                {showLabel && (
                    <div className='h-4 text-xs text-center text-gray-600'>
                        Load
                    </div>
                )}

                <div className='flex items-center justify-center'>
                    <div className='flex-1 pr-1 py-4'>
                        <ChevronLeftIcon className='ml-auto w-10 h-10 text-gray-400' />
                    </div>

                    <div className="w-20 h-20 bg-gray-200 rounded-xl animate-pulse" />

                    <div className='flex-1 pl-1 py-4'>
                        <ChevronRightIcon className='mr-auto w-10 h-10 text-gray-400' />
                    </div>
                </div>

            </div>
        )
    }

    return (
        <div className='space-y-2'>
            {showLabel && (
                <div className='h-4 text-xs text-center text-gray-600'>
                    Load
                </div>
            )}

            <div className='flex items-center justify-center'>
                {disableCardInputs === false &&
                    <div 
                        className='flex-1 pr-1 py-4' // bg-red-100
                        onClick={() => DecrementLoadCompleted()}
                    >
                        <ChevronLeftIcon className='ml-auto w-10 h-10 text-gray-600' />
                    </div>
                }

                <div 
                    className={`
                        relative 
                        ${isScaling ? 'animate-scale-bounce' : ''}
                    `}
                >
                    <input 
                        type="text" 
                        inputMode="numeric"
                        className='w-20 h-20 rounded-xl flex items-center justify-center text-4xl text-center text-gray-900 pb-3 border border-gray-400 bg-white'
                        value={isInputBoxCurrentlyFocused ? inputBoxValue : String(weightRecommendationRounded)} 
                        disabled={disableCardInputs}
                        onFocus={HandleInputBoxFocus}
                        onChange={(event) => HandleLoadCompletedChange(event.target.value)}
                        onBlur={HandleLoadCompletedBlur}
                    />

                    <div className='absolute bottom-2 left-0 w-full text-center text-xs text-gray-800'>
                        lbs
                    </div>
                </div>

                {disableCardInputs === false &&
                    <div 
                        className='flex-1 pl-1 py-4' // bg-red-100
                        onClick={() => IncrementLoadCompleted()}
                    >
                        <ChevronRightIcon className='mr-auto w-10 h-10 text-gray-600' />
                    </div>
                }
            </div>
        </div>
    )
}